import IBiometricProvider from './IBiometricProvider';
import {
  prepareCredentialCreationOptions,
  serializeAttestationResponse,
  prepareAssertionOptions,
  serializeAssertionResponse,
} from './webAuthnUtils';

/**
 * Standard W3C Web Authentication API Provider.
 * Works seamlessly across modern browsers supporting Windows Hello,
 * macOS Touch ID, Face ID, Android Biometrics, and USB Security Keys.
 */
export class WebAuthnProvider extends IBiometricProvider {
  constructor() {
    super();
    this.name = 'WebAuthn Platform / Security Key';
  }

  isWebAuthnSupported() {
    return Boolean(
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential === 'function' &&
      navigator.credentials &&
      typeof navigator.credentials.create === 'function' &&
      typeof navigator.credentials.get === 'function'
    );
  }

  async isAvailable() {
    return this.isWebAuthnSupported();
  }

  async isPlatformAuthenticatorAvailable() {
    if (!this.isWebAuthnSupported()) return false;
    try {
      if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      }
      return false;
    } catch {
      return false;
    }
  }

  async getDeviceInfo() {
    const available = await this.isAvailable();
    const hasPlatformAuth = await this.isPlatformAuthenticatorAvailable();

    const isWindows = navigator.userAgent.includes('Windows');
    const isMac = navigator.userAgent.includes('Mac');

    let providerLabel = 'Passkey / Platform Authenticator';
    if (isWindows && hasPlatformAuth) {
      providerLabel = 'Windows Hello (Fingerprint / PIN / Face)';
    } else if (isMac && hasPlatformAuth) {
      providerLabel = 'Touch ID / Apple Passkey';
    }

    return {
      providerName: providerLabel,
      deviceType: hasPlatformAuth ? 'Platform' : 'SecurityKeyOrPasskey',
      supportsBiometrics: hasPlatformAuth,
      isAvailable: available,
    };
  }

  async register(serverOptions) {
    if (!(await this.isAvailable())) {
      throw new Error('WebAuthn / Passkey is not supported in this browser or environment.');
    }

    const publicKeyOptions = prepareCredentialCreationOptions(serverOptions);

    try {
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      });

      if (!credential) {
        throw new Error('Passkey creation was canceled or not returned by device.');
      }

      return serializeAttestationResponse(credential);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        throw new Error('Registration canceled or timed out. Please verify with your fingerprint, PIN, or security key.');
      }
      if (err.name === 'InvalidStateError') {
        throw new Error('This authenticator is already registered for your account.');
      }
      throw new Error(err.message || 'Passkey registration ceremony failed.');
    }
  }

  async authenticate(serverOptions) {
    if (!(await this.isAvailable())) {
      throw new Error('WebAuthn / Passkey is not supported in this browser or environment.');
    }

    const publicKeyOptions = prepareAssertionOptions(serverOptions);

    try {
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      if (!assertion) {
        throw new Error('Authentication was canceled or not returned by device.');
      }

      return serializeAssertionResponse(assertion);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        throw new Error('Authentication canceled or timed out. Please try again.');
      }
      throw new Error(err.message || 'Passkey verification failed.');
    }
  }
}

export default WebAuthnProvider;
